import React, { useState, useEffect, useCallback } from 'react';
import { Spin } from 'antd';
import { CheckCircle, Warning, Circle, Sun, BatteryCharging, HouseLine, List } from '@phosphor-icons/react';
import { useOneButtonStore, COMMON_CONSTANTS } from '../../../../store/oneButtonStore';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import './oneButton-css/FinancePartnerCreateLeadProgressModal.css';
import { OBD_FINANCE_PARTNER_PROGRESS_Z } from './oneButtonOverlayZ';
import { SyncStep, ProductSyncState, FinancePartnerCreateLeadProgressModalProps } from '../../../../@types/oneButtonType';
import opportunityInfoServices from '../../../../services/oneButtonServices/opportunityInfoService';
import DashboardModal from './DashboardModal';
import {
    getContractApiErrorMessage,
    getContractApiResultErrorMessage,
} from '../../../../helpers/contractApiErrorMessage';
import { usePartnerApiErrorStore } from '../../../../store/partnerApiErrorStore';

const INITIAL_STEPS = (partnerName: string, action: string = 'create', resyncStepId?: string): SyncStep[] => {
    const steps: SyncStep[] = [
        // These are the steps we follow to sync data with the finance partner.
        { id: 'lead', label: action === 'sync' ? 'Sync Lead' : 'Create Lead', status: 'pending' },
        { id: 'contact', label: 'Create Contact', status: 'pending' },
        { id: 'title', label: 'Check Title', status: 'pending' }
    ];

    // Only add Utility and Lead System Sync if the partner is IGS OR LIGHTREACH AND we are in the automation/resync flow.
    // The "create" and "sync" flows should only handle Lead/Contact/Title as per the old flow.
    const upName = partnerName.toUpperCase();
    if ((upName === 'IGS' || upName === 'LIGHTREACH') && action === 'automation_sync') {
        steps.push({ id: 'utility', label: 'Update Utilities', status: 'pending' });
        
        // As requested: "if i click Utility sync or resync no need to show Lead System sync section"
        if (resyncStepId !== 'utility') {
            steps.push({ id: 'leadSystem', label: 'Create Lead System', status: 'pending' });
        }
    }

    // Filter steps to show only up to the requested resync step
    if (resyncStepId === 'titleCheck') {
        const titleIdx = steps.findIndex(s => s.id === 'title');
        if (titleIdx !== -1) return steps.slice(0, titleIdx + 1);
    }
    if (resyncStepId === 'contact') {
        const contactIdx = steps.findIndex(s => s.id === 'contact');
        if (contactIdx !== -1) return steps.slice(0, contactIdx + 1);
    }

    return steps;
};

export const FinancePartnerCreateLeadProgressModal: React.FC<FinancePartnerCreateLeadProgressModalProps & { resyncStepId?: string }> = ({
    open,
    onClose,
    opportunityId,
    partnerName,
    products,
    action = 'create',
    syncPayload,
    resyncStepId
}) => {
    const theme = useOneButtonStore((s) => s.theme); // Get the current theme (light or dark) from the store.
    const [productStates, setProductStates] = useState<ProductSyncState[]>([]); // Keep track of the progress for each product like Solar or Battery.
    const [isComplete, setIsComplete] = useState(false); // This tells us when all the steps are finished successfully.
    const [overallError, setOverallError] = useState<string | null>(null); // Store any error message if something goes wrong during the sync.

    const { SolarProductName, RoofProductName, BatteryProductName, RRRProductName } = COMMON_CONSTANTS;

    const handleCloseWrapper = () => {
        // Instantly trigger background fetch
        (async () => {
            try {
                const state = useOneButtonStore.getState();
                const salesforceId = state.opportunityDetails?.SalesForce_Id || state.opportunityDetails?.Salesforce_Id || opportunityId;
                if (salesforceId) {
                    const oppDetail = await opportunityInfoServices.getOpportunityDetail(String(salesforceId));
                    if (oppDetail) state.setOpportunityDetails(oppDetail);
                }
            } catch (err) {
                console.error('Failed to auto-refresh opp details on close:', err);
            }
        })();
        
        // Unmount instantly
        if (onClose) onClose();
    };

    // Helper to map long product names/types to the short codes used by the finance partner APIs.
    const getNormalizationCode = (type: string) => {
        const t = (type || '').toLowerCase();
        if (t.includes(SolarProductName.toLowerCase())) return 'S';
        if (t.includes(RoofProductName.toLowerCase())) return 'R';
        if (t.includes(BatteryProductName.toLowerCase())) return 'B';
        if (t.includes(RRRProductName.toLowerCase())) return 'RRR';
        return 'S'; // Default to Solar if unknown
    };

    const updateProductStepStatus = (productIndex: number, stepId: string, status: SyncStep['status'], message?: string) => {
        setProductStates(prev => {
            const next = [...prev];
            const currentProduct = next[productIndex];
            if (!currentProduct) return prev;

            next[productIndex] = {
                ...currentProduct,
                steps: currentProduct.steps.map(step => 
                    step.id === stepId ? { ...step, status, message } : step
                )
            };
            return next;
        });
    };

    // This is the main function that runs all the sync steps one by one.
    const runSyncSequence = useCallback(async () => {
        try {
            const { SolarProductName, RoofProductName, BatteryProductName, RRRProductName } = COMMON_CONSTANTS;

            // Helper to check if an API response is successful (handles multiple formats)
            const isResponseSuccess = (res: any) => {
                if (res === true || res === "true") return true;
                if (!res || typeof res !== 'object') return false;
                return res.IsSuccessful === true || 
                       res.isSuccesful === true || 
                       res.isSuccessful === true || 
                       res.IsSuccessful === "true" ||
                       res.isSuccesfull === true ||
                       res.isSuccessfull === true ||
                       res.Message?.toLowerCase().includes('success');
            };

            // Step 1: Force reset sync data if we are doing a fresh "Create" or "Sync" from lead selection.
            if (action === 'create' || action === 'sync') {
                await quoteServices.resetSyncData(opportunityId, syncPayload || []);
            }

            // Step 2: Access current sync status from store ONLY if we are in the automation/checklist resync flow.
            let syncList = useOneButtonStore.getState().financePartnerRawSyncStatuses || [];
            if (action === 'automation_sync') {
                // We use the already kept value from oneButton store as requested.
                // If it's empty, we might do a fallback fetch if necessary, but according to requirement we just use the store.
                if (!Array.isArray(syncList) || syncList.length === 0) {
                    const res = await quoteServices.getSyncStatusList(opportunityId);
                    syncList = res?.FinancePartnerSyncStatuses || (Array.isArray(res) ? res : []);
                    useOneButtonStore.getState().setFinancePartnerRawSyncStatuses(syncList);
                }
            }
            
            const isStepDoneResult = (stepId: string, currentProductType: string) => {
                // If it's a full create/sync operation, we never skip steps.
                if (action !== 'automation_sync') return false;

                if (!Array.isArray(syncList) || syncList.length === 0) return false;
                
                // Usually there is one object per product type, but sometimes we just want to know if it's done overall.
                const match = syncList.find((s: any) => 
                    (s.ProductType === currentProductType || s.productType === currentProductType) || 
                    (!currentProductType && s)
                );
                
                if (!match) return false;

                // Handle the boolean flags from the API (IsLeadCreate, IsContactSync, IsTilte, IsUtility, IsLeadSystem, IsCreditPass)
                switch(stepId) {
                    case 'lead': return match.IsLeadCreate === true;
                    case 'contact': return match.IsContactSync === true;
                    case 'title': return (match.IsTilte === true || match.IsTitle === true);
                    case 'utility': return match.IsUtility === true;
                    case 'leadSystem': return match.IsLeadSystem === true;
                    case 'credit': return match.IsCreditPass === true;
                    default: return false;
                }
            };

            const isStepDone = (stepId: string, currentProductType: string) => {
                if (action !== 'automation_sync') return false;

                // If this is the specific step we want to resync, pretend it's not done.
                // Map checklist IDs to progress IDs where they differ
                if (resyncStepId && (
                    resyncStepId === stepId || 
                    (resyncStepId === 'titleCheck' && stepId === 'title')
                )) {
                    return false;
                }
                return isStepDoneResult(stepId, currentProductType);
            };

            // Loop through each product to sync leads, contacts, and titles.
            for (let i = 0; i < products.length; i++) {
                const { productType } = products[i];

                // Step 2: Make sure a lead exists; if not, create a new one.
                const isLeadDone = isStepDone('lead', productType);
                if (isLeadDone) {
                    updateProductStepStatus(i, 'lead', 'success');
                } else {
                    updateProductStepStatus(i, 'lead', 'syncing');
                    
                    // In the old flow, even for sync, we just verify the lead exists or is refreshed.
                    const leadCheck = await quoteServices.isLeadCreated(opportunityId, productType);
                    
                    if (action === 'create' && !leadCheck?.IsSuccessful) {
                        const productCode = getNormalizationCode(productType);
                        const createRes = await quoteServices.createLead({
                            opportunityId,
                            productSelection: { Product_Category: productCode, LeadForProduct: productCode }
                        });
                        if (createRes === "" || createRes?.IsSuccessful === false) {
                            throw new Error(
                                getContractApiResultErrorMessage(
                                    createRes,
                                    `Lead creation failed for ${productType}`,
                                ),
                            );
                        }
                    }
                    updateProductStepStatus(i, 'lead', 'success');
                }

                // Step 3: Sync the contact details with the finance partner.
                // As requested: ensure bottom steps sync api is not trigering
                if (resyncStepId === 'lead') continue;

                const isContactDone = isStepDone('contact', productType);
                if (isContactDone) {
                    updateProductStepStatus(i, 'contact', 'success');
                } else {
                    updateProductStepStatus(i, 'contact', 'syncing');
                    await quoteServices.isContactCreated(opportunityId, productType);
                    await quoteServices.createContactFinancePartner(opportunityId, productType, true, action === 'sync');
                    updateProductStepStatus(i, 'contact', 'success');
                }

                // Step 4: Check if the title is verified by the partner.
                // As requested: ensure bottom steps sync api is not trigering
                if (resyncStepId === 'contact' || resyncStepId === 'lead') continue;

                const isTitleDone = isStepDone('title', productType);
                if (isTitleDone) {
                    updateProductStepStatus(i, 'title', 'success');
                } else {
                    updateProductStepStatus(i, 'title', 'syncing');
                    try {
                        const titleRes = await quoteServices.financePartnerTitleCheck(opportunityId, productType);
                        
                        const isTitleSuccess = titleRes === true || 
                                             titleRes?.isSuccesful === true || 
                                             titleRes?.IsSuccessful === true || 
                                             titleRes?.isSuccessful === true ||
                                             titleRes?.Message?.toLowerCase().includes('success');

                        if (!isTitleSuccess) {
                            // If title check fails, we mark it as error but DO NOT throw, so the process continues
                            updateProductStepStatus(i, 'title', 'error', titleRes?.Message || `Title verification failed for ${productType}`);
                        } else {
                            updateProductStepStatus(i, 'title', 'success');
                        }
                    } catch (err: any) {
                        // Even on network error for title check, we continue
                        updateProductStepStatus(i, 'title', 'error', err?.message || 'Title check connection error');
                    }
                }

                // Step 5: Sync utility data with the finance partner if it is IGS or LIGHTREACH.
                const upName = partnerName.toUpperCase();
                if ((upName === 'IGS' || upName === 'LIGHTREACH') && action === 'automation_sync') {
                    // As requested: "after clicking Title check all the remaining section are showing only need to shoup up to Check Tiltle"
                    if (resyncStepId === 'titleCheck' || resyncStepId === 'contact' || resyncStepId === 'lead') continue;

                    const isUtilDone = isStepDone('utility', productType);
                    if (isUtilDone) {
                        updateProductStepStatus(i, 'utility', 'success');
                    } else {
                        // If we are specifically resyncing Lead System, we DO NOT trigger utility sync here.
                        // We will check its status in the next step.
                        updateProductStepStatus(i, 'utility', 'syncing');
                        
                        let utilitySuccess = false;
                        let retryCount = 0;
                        const maxRetries = 5;
                        const isRoofOnly = productType.toUpperCase() === 'RO' || productType.toUpperCase() === 'ROOF ONLY' || productType.toUpperCase() === 'ROOF';
                        
                        let utilityResponse:any;
                        while (!utilitySuccess && retryCount < maxRetries) {
                            try {
                                const utilRes = await quoteServices.updateUtilitiesSunnova(opportunityId, isRoofOnly, productType);
                                if (isResponseSuccess(utilRes)) {
                                    utilitySuccess = true;
                                } else {
                                    retryCount++;
                                    if (retryCount < maxRetries) await new Promise(r => setTimeout(r, 1000));
                                    else utilityResponse = utilRes
                                }
                            } catch (e) {
                                retryCount++;
                                if (retryCount < maxRetries) await new Promise(r => setTimeout(r, 1000));
                            }
                        }

                        if (!utilitySuccess) {
                            const message = JSON.parse(utilityResponse)?.message ?? `There was an error syncing Utility Information to ${partnerName}, please ensure the correct Utility Company and Installation Address/ Zip code has been selected, ensure that the 'Current All In Utility Rate' and 'Annual Usage' values have been entered.`;
                            throw new Error(message);
                        }
                        updateProductStepStatus(i, 'utility', 'success');
                    }
                }

                // Step 6: Create the Lead System record (the final handshake).
                if ((upName === 'IGS' || upName === 'LIGHTREACH') && action === 'automation_sync') {
                    // As requested: "if i click Utility sync or resync no need to show Lead System sync section and don't triger it's sync api"
                    if (resyncStepId === 'utility') continue;

                    const isLeadSystemDone = isStepDone('leadSystem', productType);
                    if (isLeadSystemDone) {
                        updateProductStepStatus(i, 'leadSystem', 'success');
                    } else {
                        updateProductStepStatus(i, 'leadSystem', 'syncing');
                        
                        let systemSuccess = false;
                        let sRetryCount = 0;
                        const sMaxRetries = 5;
                        
                        while (!systemSuccess && sRetryCount < sMaxRetries) {
                            try {
                                const productCode = getNormalizationCode(productType);
                                const opportunityProductType = useOneButtonStore.getState().productType;
                                
                                // The API expects the payload in a specific order for some environments.
                                const payload = {
                                    productSelection: {
                                        contractType: '',
                                        Product_Category: opportunityProductType || productCode,
                                        LeadForProduct: productCode
                                    },
                                    opportunityId: opportunityId
                                };

                                const sysRes = await quoteServices.createLeadSystem(payload);
                                if (isResponseSuccess(sysRes)) {
                                    // Step 7 Success: Handle Multi-Contract logic and Fetch finalized partner system IDs.
                                    const oppDetails = useOneButtonStore.getState().opportunityDetails;
                                    const isMultiContract = oppDetails?.IsMultiContract;
                                    const isRoofOnly = oppDetails?.OpportunityInterest?.RoofOnly;

                                    if (isMultiContract && isRoofOnly) {
                                        await quoteServices.checkIgnoreTitle(opportunityId);
                                        await quoteServices.creditRollover(opportunityId);
                                    }
                                    
                                    // Always call displayContacts to finalize the sync in our backend
                                    await opportunityInfoServices.displayContacts(opportunityId);
                                    
                                    systemSuccess = true;
                                } else {
                                    sRetryCount++;
                                    if (sRetryCount < sMaxRetries) await new Promise(r => setTimeout(r, 1000));
                                }
                            } catch (e) {
                                sRetryCount++;
                                if (sRetryCount < sMaxRetries) await new Promise(r => setTimeout(r, 1000));
                            }
                        }

                        if (!systemSuccess) {
                            throw new Error("Failed to create the Lead System in the finance partner portal after multiple retries. Please verify the system design is complete and try again.");
                        }
                        updateProductStepStatus(i, 'leadSystem', 'success');
                    }
                }

                // Save the final sync status into our global store.
                const newSyncStatus: any = {
                    leadSyncAction: action === 'sync' ? 'sync_existing' : 'create',
                    lead: true,
                    contact: true,
                    titleCheck: true,
                };
                
                // Only mark utility and leadSystem true if we actually intended to sync it or it's part of this flow
                if ((upName === 'IGS' || upName === 'LIGHTREACH') && action === 'automation_sync') {
                    newSyncStatus.utility = true;
                    newSyncStatus.leadSystem = true;
                }

                useOneButtonStore.getState().setFinancePartnerSyncStatus(partnerName, newSyncStatus);
            }

            const upName = partnerName.toUpperCase();
            if (upName === 'IGS' || upName === 'LIGHTREACH') {
                useOneButtonStore.getState().setFinanceSelectionRefreshTrigger(Date.now());
            }

            setIsComplete(true);
        } catch (error: unknown) {
            const errorText = getContractApiErrorMessage(error, 'Workflow interrupted');
            setOverallError(errorText);
            setProductStates(prev => prev.map(ps => ({
                ...ps,
                steps: ps.steps.map(s => s.status === 'syncing' ? { ...s, status: 'error', message: errorText } : s)
            })));
        }
    }, [opportunityId, products, action, syncPayload, partnerName]);

    // Start the sync process automatically as soon as the modal opens.
    useEffect(() => {
        if (!open) return;
        const releaseSuppress = usePartnerApiErrorStore.getState().pushSuppressPartnerErrorPopup();
        setProductStates(products.map(p => ({
            info: p,
            steps: INITIAL_STEPS(partnerName, action, resyncStepId ?? undefined)
        })));
        setIsComplete(false);
        setOverallError(null);
        runSyncSequence();
        return releaseSuppress;
    }, [open, products, runSyncSequence, partnerName, action, resyncStepId]);

    // Pick the right icon (spinner, checkmark, or warning) based on the status.
    const getStatusIcon = (status: SyncStep['status']) => {
        switch (status) {
            case 'syncing': return <Spin size="small" />;
            case 'success': return <CheckCircle size={22} weight="fill" className="sync-icon sync-icon--success" />;
            case 'error': return <Warning size={22} weight="fill" className="sync-icon sync-icon--error" />;
            default: return <Circle size={22} weight="regular" className="sync-icon sync-icon--pending" />;
        }
    };

    // Show a nice icon like a sun for Solar or a battery for Battery.
    const getProductIcon = (type: string) => {
        const t = type.toLowerCase();
        if (t.includes('solar')) return <Sun size={14} weight="bold" />;
        if (t.includes('battery')) return <BatteryCharging size={14} weight="bold" />;
        if (t.includes('roof')) return <HouseLine size={14} weight="bold" />;
        return <List size={14} weight="bold" />;
    };

    return (
        <DashboardModal
            open={open}
            onClose={!isComplete && !overallError ? (() => {}) : handleCloseWrapper}
            footer={null}
            title={`Syncing with ${partnerName} Partner`}
            width={440}
            centered
            zIndex={OBD_FINANCE_PARTNER_PROGRESS_Z}
            theme={theme}
            className={`obd-battery-modal obd-theme-${theme}`}
            rootClassName={`obd-battery-modal-root obd-theme-${theme}`}
            maskClosable={false}
            destroyOnClose
        >
            <div className="obd-battery-modal__inner">
                <div className="obd-battery-modal__fields">
                    {/* Memoized progress bar to avoid re-calculating on every render */}
                    {React.useMemo(() => {
                        const totalSteps = productStates.reduce((acc, ps) => acc + ps.steps.length, 0);
                        const doneSteps = productStates.reduce((acc, ps) => acc + ps.steps.filter(s => s.status === 'success').length, 0);
                        const pct = totalSteps > 0 ? Math.round((doneSteps / totalSteps) * 100) : 0;
                        
                        return (
                            <div className="sync-modal-bar__wrapper">
                                <div className="sync-modal-bar__info">
                                    <span>Overall Progress</span>
                                    <span>{pct}%</span>
                                </div>
                                <div className="sync-modal-bar__bg">
                                    <div className="sync-modal-bar__fill" style={{ width: `${pct}%` }} />
                                </div>
                            </div>
                        );
                    }, [productStates])}

                    <div className="sync-modal-content__scrollable">
                        {productStates.map((productState, pIdx) => (
                            <div key={productState.info.productType} className="sync-modal-product__section">
                                <div className="sync-modal-product__badge">
                                    {getProductIcon(productState.info.productType)}
                                    <span>{productState.info.productType}</span>
                                </div>
                                
                                <div className="sync-modal-steps__container">
                                    {productState.steps.map((step, index) => (
                                        <div key={step.id} className={`sync-modal-step__item sync-modal-step--${step.status}`}>
                                            <div className="sync-modal-step__visual">
                                                <div className="sync-modal-step__icon-wrapper">
                                                    {getStatusIcon(step.status)}
                                                </div>
                                                {index !== productState.steps.length - 1 && (
                                                    <div className="sync-modal-step__line" />
                                                )}
                                            </div>
                                            <div className="sync-modal-step__text">
                                                <div className="sync-modal-step__label">{step.label}</div>
                                                {step.status === 'syncing' && <div className="sync-modal-step__status-text">Processing...</div>}
                                                {step.status === 'success' && <div className="sync-modal-step__status-text sync-modal-text--success">Complete</div>}
                                                {step.status === 'error' && <div className="sync-modal-step__status-text sync-modal-text--error">Failed</div>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {overallError && (
                    <div className="sync-modal-error__card">
                        <Warning size={20} weight="fill" />
                        <div className="sync-modal-error__content">
                            <strong>Sync Interrupted</strong>
                            <p>{overallError}</p>
                        </div>
                    </div>
                )}
            </div>
            {(isComplete || overallError) && (
                <div className="obd-sync-modal__footer progress-modal-footer">
                    <button 
                        type="button" 
                        className="obd-sync-modal__btn obd-sync-modal__btn--primary" 
                        onClick={handleCloseWrapper}
                    >
                        Close
                    </button>
                </div>
            )}
        </DashboardModal>
    );
};

export default FinancePartnerCreateLeadProgressModal;
